import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { tenantApi } from '../../lib/tenant-api';
import type { ChangePasswordRequest } from '../../lib/api-types';
import { getErrorMessage } from '../../lib/api-client';

const MIN_PASSWORD_LENGTH = 8;

export default function SecuritySection() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (data: ChangePasswordRequest) => tenantApi.changePassword(data),
    onSuccess: () => {
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setInlineError(null);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);

    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      setInlineError('All fields are required.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setInlineError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setInlineError('New password and confirmation do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setInlineError('New password must be different from your current password.');
      return;
    }

    mutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    });
  };

  return (
    <div className="bg-white">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 flex items-center">
          <ShieldCheck className="h-5 w-5 mr-2 text-indigo-600" />
          Change password
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Enter your current password and choose a new one.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-md space-y-6">
        {inlineError && (
          <div className="text-sm text-red-600" role="alert">
            {inlineError}
          </div>
        )}

        <div>
          <label htmlFor="security-current-password" className="block text-sm font-medium text-gray-700 mb-2">
            Current password
          </label>
          <div className="relative">
            <input
              id="security-current-password"
              type={showCurrentPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className="mt-1 block w-full border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2 pr-10"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={mutation.isLoading}
            />
            <button
              type="button"
              aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
              className="absolute inset-y-0 right-0 mt-1 px-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              onClick={() => setShowCurrentPassword((prev) => !prev)}
              disabled={mutation.isLoading}
            >
              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="security-new-password" className="block text-sm font-medium text-gray-700 mb-2">
            New password
          </label>
          <div className="relative">
            <input
              id="security-new-password"
              type={showNewPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="mt-1 block w-full border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2 pr-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={mutation.isLoading}
            />
            <button
              type="button"
              aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
              className="absolute inset-y-0 right-0 mt-1 px-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              onClick={() => setShowNewPassword((prev) => !prev)}
              disabled={mutation.isLoading}
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="mt-2 text-sm text-gray-500">At least {MIN_PASSWORD_LENGTH} characters.</p>
        </div>

        <div>
          <label htmlFor="security-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm new password
          </label>
          <div className="relative">
            <input
              id="security-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="mt-1 block w-full border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2 pr-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={mutation.isLoading}
            />
            <button
              type="button"
              aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              className="absolute inset-y-0 right-0 mt-1 px-3 text-gray-500 hover:text-gray-700 disabled:opacity-50"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              disabled={mutation.isLoading}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={mutation.isLoading}
        >
          {mutation.isLoading ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}
